import { ErrorHandler, ERROR_CATCH_TYPE } from "@classdojo/web/pods/errorHandling";
import { UserActionsHistory } from "@classdojo/web/pods/userActionsHistory";
import { isApiDownStatus } from "@web-monorepo/infra/responseHandlers";
import axios from "axios";
import { type Response } from "@web-monorepo/fetchers";
import { ErrorExcluder } from "@classdojo/web/pods/errorHandling/components/AppTopLevelErrorBoundary";
import { ErrorBannerValues } from "@classdojo/web/pods/errorHandling/errorBanner";
import type { ApiErrorCode } from "@web-monorepo/api-types/apiErrorCodes";
import { is307RequestBlocked, isConnectivityError, isModuleLoadError } from "@web-monorepo/molecules";
import { setLoginRedirectIfUnset } from "#/src/utils/loginRedirect";
const isAssetRetrySwActive = () => false;

type OnErrorHandler = NonNullable<ConstructorParameters<typeof ErrorHandler>[0]["onError"]>;
type ErrorHandlerUtils = Parameters<OnErrorHandler>[2];
type ErrorHandlerContext = Parameters<OnErrorHandler>[1];

const isIgnorableBadRequest = (response?: Response) => {
  if (!response) return false;
  return (
    response.status === 400 &&
    [
      "Invalid id",
      "Invalid targetId",
      "Invalid classId",
      "ChannelId was invalid",
      "Invalid marketplaceFunnelUUId",
    ].includes(response.body?.error?.detail)
  );
};

const EXPECTED_ERROR_CODES = [
  "ERR_REVIEW_ALREADY_SENT",
  "ERR_MARKETPLACE_SESSION_ALREADY_STARTED",
  "ERR_SUBSCRIPTION_ALREADY_EXISTS",
  "ERR_SUBSCRIPTION_ALREADY_CANCELLED",
  "ERR_DUPLICATE_EMAIL",
  "ERR_MARKETPLACE_INVALID_LOGIN_CODE",
  "ERR_SESSION_NOT_FULLY_ATTENDED",
  "ERR_TUTOR_SUBSTITUTION_ERROR",
];

// TODO: Some of these are real errors that we should handle elsewhere.
export const ignorableMarketplaceErrors: Array<ErrorExcluder> = [
  {
    checker: (error) => error.message.includes("You must initialize the component library with a `translate` function"),
    id: "marketplace-translate",
  },
  {
    checker: (error) =>
      axios.isAxiosError(error) && error.request?.status === 404 && error.request.responseURL?.includes("/user/email"),
    id: "marketplace-user-email",
  },
  {
    checker: (error) =>
      axios.isAxiosError(error) &&
      error.request?.status === 404 &&
      error.request.responseURL?.includes("/recap/share-url"),
    id: "marketplace-recap-share-url",
  },
  {
    checker: (error) =>
      axios.isAxiosError(error) && error.request?.status === 401 && error.request.responseURL?.includes("/session"),
    id: "marketplace-session",
  },
  {
    checker: (error) => {
      if (!axios.isAxiosError(error)) return false;
      const data = error.response?.data as { error: { code: string } } | undefined;
      return data ? EXPECTED_ERROR_CODES.includes(data.error?.code) : false;
    },
    id: "marketplace-expected-err-codes",
  },
  // CDN Cookielaw errors (OneTrust cookie consent)
  // Catches all cdn.cookielaw.org resources (.js, .json, .svg, etc.) - these fail due to ad blockers or network issues
  {
    checker: (error) => !!error.stack?.match(/cdn\.cookielaw\.org/) || error.message.includes("cdn.cookielaw.org"),
    id: "marketplace-cookielaw",
  },
  // Swallowing errors from empty event attack
  {
    checker: (error) =>
      error.message.includes("Cannot read properties of undefined (reading 'target')") &&
      error.message.includes("at Element.addEventListener") &&
      error.message.includes("at Set.forEach"),
    id: "marketplace-empty-event",
  },
];

// Connectivity failures (including API errors with status 0 — no response received)
// get the no-network page, reported as app_broken source network_fallback instead of
// the error_banner/unknown bucket. Matches home and teach.
function handleConnectivityError(error: Error, errorHandlerUtils: ErrorHandlerUtils) {
  if (!isConnectivityError(error)) {
    return false;
  }

  errorHandlerUtils.showErrorBanner(ErrorBannerValues.noNetwork);
  return true;
}

function handleRequestBlockedError(error: Error, errorHandlerUtils: ErrorHandlerUtils) {
  if (!is307RequestBlocked(error)) {
    return false;
  }

  errorHandlerUtils.showErrorBanner(ErrorBannerValues.requestBlocked);
  return true;
}

function handleModuleLoadError(error: Error, errorHandlerUtils: ErrorHandlerUtils) {
  if (!isModuleLoadError(error)) {
    return false;
  }

  errorHandlerUtils.showErrorBanner(ErrorBannerValues.moduleLoadError, window.location.pathname + window.location.hash);
  return true;
}

function handleIgnorableBadRequest(error: Error, response: Response | undefined, errorHandlerUtils: ErrorHandlerUtils) {
  if (!isIgnorableBadRequest(response)) {
    return false;
  }

  errorHandlerUtils.logRequestErrorWithContext("badRequest", error);
  errorHandlerUtils.showErrorBanner(ErrorBannerValues.badRequest, "/");
  return true;
}

function handleAuthErrors(error: Error, response: Response | undefined, errorHandlerUtils: ErrorHandlerUtils) {
  if (!response || ![401, 403].includes(response.status)) {
    return false;
  }

  // On 401 (not authenticated) save the current location so the user is redirected after logging in
  if (response.status === 401) {
    setLoginRedirectIfUnset();
  }

  errorHandlerUtils.logRequestErrorWithContext(response.status === 401 ? "auth.401" : "auth.403", error);
  errorHandlerUtils.showErrorBanner(
    response.status === 401 ? ErrorBannerValues.loggedOut : ErrorBannerValues.forbidden,
    "/#/login",
  );
  return true;
}

function handleNotFoundError(error: Error, response: Response | undefined, errorHandlerUtils: ErrorHandlerUtils) {
  if (!response || response.status !== 404) {
    return false;
  }

  errorHandlerUtils.logRequestErrorWithContext("request.404", error);
  errorHandlerUtils.showErrorBanner(ErrorBannerValues.notFound);
  return true;
}

function handleRateLimitError(error: Error, response: Response | undefined, errorHandlerUtils: ErrorHandlerUtils) {
  if (!response || response.status !== 429) {
    return false;
  }

  errorHandlerUtils.logRequestErrorWithContext("rateLimit", error);
  errorHandlerUtils.showErrorBanner(ErrorBannerValues.rateLimit);
  return true;
}

function handleApiDownError(error: Error, response: Response | undefined, errorHandlerUtils: ErrorHandlerUtils) {
  if (!response || !isApiDownStatus(response.status)) {
    return false;
  }

  errorHandlerUtils.logRequestErrorWithContext("down", error);
  const statusCode = response.statusCode;
  const errorMessage = response.body?.error?.message || "";
  const isLoadSheddingError = errorMessage.toLowerCase().includes("load shedding");

  if (statusCode === 503 && isLoadSheddingError) {
    errorHandlerUtils.showErrorBanner(ErrorBannerValues.apiDownLoadShedding);
  } else {
    errorHandlerUtils.showErrorBanner(ErrorBannerValues.apiDown);
  }
  return true;
}

function handleCatchType({
  error,
  context,
  errorHandlerUtils,
}: {
  error: Error;
  context: ErrorHandlerContext;
  errorHandlerUtils: ErrorHandlerUtils;
}) {
  switch (context.catchType) {
    case ERROR_CATCH_TYPE.REACT_ERROR_BOUNDARY: {
      errorHandlerUtils.logExceptionWithContext(error);
      errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown);
      break;
    }
    case ERROR_CATCH_TYPE.UNHANDLED_REJECTION: {
      errorHandlerUtils.logExceptionWithContext(error);
      break;
    }
    case ERROR_CATCH_TYPE.MODULE_LOAD_ERROR: {
      errorHandlerUtils.showErrorBanner(
        ErrorBannerValues.moduleLoadError,
        window.location.pathname + window.location.hash,
      );
      break;
    }
    // Handles REDUX and SAGAS errors:
    default: {
      errorHandlerUtils.logExceptionWithContext(error);
      errorHandlerUtils.showErrorBanner(ErrorBannerValues.unknown);
    }
  }
}

export const createWebErrorHandler = (userActionsHistory: UserActionsHistory) => {
  return new ErrorHandler({
    userActionsHistory,
    isAssetRetrySwActive,
    onError: (error, context, errorHandlerUtils) => {
      const response = isApiError(error) ? error.response : undefined;

      if (handleConnectivityError(error, errorHandlerUtils)) return;
      if (handleRequestBlockedError(error, errorHandlerUtils)) return;
      if (handleModuleLoadError(error, errorHandlerUtils)) return;
      if (handleIgnorableBadRequest(error, response, errorHandlerUtils)) return;
      if (handleAuthErrors(error, response, errorHandlerUtils)) return;
      if (handleNotFoundError(error, response, errorHandlerUtils)) return;
      if (handleRateLimitError(error, response, errorHandlerUtils)) return;
      if (handleApiDownError(error, response, errorHandlerUtils)) return;

      handleCatchType({ error, context, errorHandlerUtils });
    },
  });
};

const isApiError = (error: Error): error is Error & { response: Response } => "response" in error;

export interface ApiErrorBody {
  type?: string | number;
  message?: string;
  detail?: string;
  code?: ApiErrorCode;
  fallbackMessage?: string;
  expected?: boolean;
}
